import { useEffect } from 'react';
import { releaseFetch } from '@/lib/release/request';
import { checkListingsPageOriginResponse, withListingsPageOrigin } from './listingsPageOrigin';

/**
 * Seam `pageSourceOverride` recovery for any folded surface.
 *
 * During the traffic-ramp dark deploy, the seam's viewer-request function
 * denies host-only BFF paths (`/api/<surface>/v1/**` — paths only the eb-web
 * host serves) with a generated 404 unless the request rides a valid
 * `pageSourceOverride` cookie (or the ramp is above 0%). The cookie is
 * short-lived, so a page kept open past its expiry loses every API call.
 * This module re-mints the cookie client-side — without navigation — and
 * retries the failed call once. It is host-level code: every folded surface
 * shares it, and its only surface-specific input is the document URL the
 * re-mint fetches.
 *
 * Rollback safety: minting an override forces NEW for every surface while it
 * lives, so a ramped (non-tester) user must never self-mint one past a ramp
 * rollback. Every path below is therefore gated on `overrideSessionEnabled`,
 * which only the server can set (gSSP reads the HttpOnly cookie at render
 * time and passes `hasOverrideSession` down — the client cannot forge it).
 */

const OVERRIDE_PARAM = 'pageSource';
const OVERRIDE_DIRECTION = 'eb-web';
/**
 * Host-owned BFF path shapes the seam can deny:
 * - `/api/**`      the canonical host namespace (incl. `/api/listings/**`).
 * - `/e/api`       the listings surface's browser-visible basePath BFF.
 * A legacy `/conversions-manager/api/...` style path is deliberately NOT here:
 * it is served by OLD, so the seam never denies it.
 */
const BFF_PATH_PREFIXES = ['/api', '/e/api'] as const;

function isBffPath(url: string): boolean {
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    pathname = url.split(/[?#]/, 1)[0] || '';
  }
  return BFF_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

let overrideSessionEnabled = false;

export function setOverrideSessionEnabled(enabled: boolean): void {
  overrideSessionEnabled = enabled;
}

/**
 * The seam's deny response is a generated 404 with an empty body and no
 * content-type (the BFF's own 404s are JSON; Next's are HTML).
 */
export function isSeamOverrideDenied(response: Response): boolean {
  if (response.status !== 404) return false;
  if (!isBffPath(response.url)) return false;
  const length = response.headers.get('content-length');
  return (
    response.headers.get('content-type') === null &&
    (length === null || length === '0')
  );
}

/**
 * Re-mints the override cookie by fetching the current document with the
 * override query parameter. The seam answers that parameter with a 307 whose
 * Set-Cookie re-mints the cookie; `redirect: 'manual'` keeps the browser from
 * navigating, so form state survives.
 *
 * Success is detected strictly via the opaque-redirect mint response: a plain
 * 200 means the gate did not recognize the parameter and no cookie was
 * minted. That is the expected outcome under the HMAC-signed seam build
 * (which accepts only `direction|expiry|hexHmac` claims): the app holds no
 * signing key by design, so it cannot mint a signed claim, and an idle
 * tester's session in that world is kept alive by the seam's viewer-response
 * sliding renewal (edge/traffic-seam/viewer-response.js) instead. Returns false so
 * callers skip the futile retry.
 */
export async function remintOverrideCookie(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(OVERRIDE_PARAM, OVERRIDE_DIRECTION);
    const response = await fetch(url.toString(), {
      redirect: 'manual',
      credentials: 'same-origin',
    });
    return response.type === 'opaqueredirect';
  } catch {
    // Best-effort client recovery: no host logger here. The shared logger is
    // server-only (its tracing pulls Node modules into the bundle, which
    // Turbopack rejects for client chunks), and the fold logger is
    // surface-local. A failed remint is invisible-but-harmless: the caller
    // skips the retry, and the keepalive ticks again within the interval.
    console.debug('pageSourceOverride remint failed');
    return false;
  }
}

/**
 * Keepalive cadence for an override session: re-mint well inside the seam
 * cookie's TTL so a page left open (even AFK for hours) never loses BFF
 * access. The visibility hook also covers a laptop waking from sleep.
 */
const OVERRIDE_KEEPALIVE_INTERVAL_MS = 30 * 60 * 1000;

/**
 * Mount-once keepalive for a server-confirmed override session. Never arms
 * for a ramped user (`hasOverrideSession` comes from gSSP reading the
 * HttpOnly cookie — the client cannot forge it).
 */
export function usePageSourceOverrideKeepalive(
  hasOverrideSession: boolean | undefined,
): void {
  useEffect(() => {
    if (!hasOverrideSession) return;
    if (typeof window === 'undefined') return;

    const remint = () => {
      void remintOverrideCookie();
    };

    remint();
    const timer = setInterval(remint, OVERRIDE_KEEPALIVE_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') remint();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hasOverrideSession]);
}

/**
 * `releaseFetch` with one-shot seam-override recovery: on a seam-deny 404
 * (only in a server-confirmed override session), re-mints the cookie and
 * retries the identical request once.
 */
export async function releaseFetchWithOverrideRecovery(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const pinnedInit = withListingsPageOrigin(input, init);
  const first = await releaseFetch(input, pinnedInit);
  checkListingsPageOriginResponse(first);
  if (!overrideSessionEnabled || !isSeamOverrideDenied(first)) {
    return first;
  }
  const reminted = await remintOverrideCookie();
  if (!reminted) {
    return first;
  }
  const recovered = await releaseFetch(input, pinnedInit);
  checkListingsPageOriginResponse(recovered);
  return recovered;
}
